import React, { useState, useEffect, useCallback } from 'react';
import { apiGet, apiPost, apiPatch } from '../../lib/api';
import { useAuthStore } from '../../stores/auth.store';
import { StaffRole } from '@bisa/shared';
import { Button } from '../../components/ui/Button';
import { Input, Select } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { PageSpinner } from '../../components/ui/Spinner';

interface StaffMember {
  id: string;
  userId: string;
  email?: string;
  role: StaffRole;
  isActive: boolean;
  createdAt: string;
}

interface AddForm {
  email: string;
  role: StaffRole;
  tempPassword: string;
}

interface EditForm {
  role: StaffRole;
  isActive: boolean;
}

const emptyAdd: AddForm = { email: '', role: StaffRole.FRONT_DESK, tempPassword: '' };

export function StaffPage() {
  const { staff } = useAuthStore();
  const facilityId = staff!.facilityId;

  const [members, setMembers] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState<AddForm>(emptyAdd);
  const [addSaving, setAddSaving] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [addedTempPw, setAddedTempPw] = useState<string | null>(null);
  const [addedEmail, setAddedEmail] = useState<string | null>(null);

  const [resetLinkFor, setResetLinkFor] = useState<StaffMember | null>(null);
  const [resetLink, setResetLink] = useState<string | null>(null);
  const [resetLinkLoading, setResetLinkLoading] = useState(false);
  const [resetLinkError, setResetLinkError] = useState<string | null>(null);
  const [resetLinkCopied, setResetLinkCopied] = useState(false);

  const [showEdit, setShowEdit] = useState(false);
  const [editingMember, setEditingMember] = useState<StaffMember | null>(null);
  const [editForm, setEditForm] = useState<EditForm>({ role: StaffRole.FRONT_DESK, isActive: true });
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiGet<StaffMember[]>(`/admin/facilities/${facilityId}/staff`);
      setMembers(data);
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }, [facilityId]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddSaving(true);
    setAddError(null);
    try {
      await apiPost(`/admin/facilities/${facilityId}/staff`, addForm);
      setAddedTempPw(addForm.tempPassword);
      setAddedEmail(addForm.email);
      setAddForm(emptyAdd);
      await load();
    } catch (e) {
      setAddError(String(e));
    } finally {
      setAddSaving(false);
    }
  };

  const openResetLink = async (m: StaffMember) => {
    setResetLinkFor(m);
    setResetLink(null);
    setResetLinkError(null);
    setResetLinkCopied(false);
    setResetLinkLoading(true);
    try {
      const { link } = await apiPost<{ link: string }>(`/admin/facilities/${facilityId}/staff/${m.id}/reset-link`);
      setResetLink(link);
    } catch (e) {
      setResetLinkError(String(e));
    } finally {
      setResetLinkLoading(false);
    }
  };

  const openEdit = (m: StaffMember) => {
    setEditingMember(m);
    setEditForm({ role: m.role, isActive: m.isActive });
    setEditError(null);
    setShowEdit(true);
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingMember) return;
    setEditSaving(true);
    setEditError(null);
    try {
      await apiPatch(`/admin/facilities/${facilityId}/staff/${editingMember.id}`, editForm);
      setMembers((prev) =>
        prev.map((m) =>
          m.id === editingMember.id ? { ...m, role: editForm.role, isActive: editForm.isActive } : m,
        ),
      );
      setShowEdit(false);
    } catch (e) {
      setEditError(String(e));
    } finally {
      setEditSaving(false);
    }
  };

  const roleColors: Record<StaffRole, string> = {
    [StaffRole.FRONT_DESK]: 'text-blue-700 bg-blue-50',
    [StaffRole.PROVIDER]: 'text-purple-700 bg-purple-50',
    [StaffRole.ADMIN]: 'text-green-700 bg-green-50',
  };

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">Staff</h1>
        <Button onClick={() => { setShowAdd(true); setAddedTempPw(null); }}>+ Add Staff</Button>
      </div>

      {loading && <PageSpinner />}
      {error && <p className="text-red-600">{error}</p>}

      {!loading && !error && (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {['Email', 'Role', 'Active', 'Since', ''].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {members.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400">No staff members yet.</td></tr>
              )}
              {members.map((m) => (
                <tr key={m.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-900">{m.email ?? m.userId}</td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${roleColors[m.role]}`}>
                      {m.role}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-medium ${m.isActive ? 'text-green-600' : 'text-gray-400'}`}>
                      {m.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-400">
                    {new Date(m.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1.5">
                      <Button size="sm" variant="secondary" onClick={() => openEdit(m)}>Edit</Button>
                      <Button size="sm" variant="ghost" onClick={() => openResetLink(m)}>Reset PW</Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add staff modal */}
      <Modal
        open={showAdd}
        title="Add Staff Member"
        onClose={() => { setShowAdd(false); setAddError(null); setAddedTempPw(null); }}
        footer={
          addedTempPw ? (
            <Button onClick={() => { setShowAdd(false); setAddedTempPw(null); }}>Done</Button>
          ) : (
            <>
              <Button variant="secondary" onClick={() => setShowAdd(false)}>Cancel</Button>
              <Button form="add-staff-form" type="submit" loading={addSaving}>Create User</Button>
            </>
          )
        }
      >
        {addedTempPw ? (
          <div className="space-y-3">
            <p className="text-sm font-medium text-gray-800">User created — share these credentials:</p>
            <div className="rounded-md bg-yellow-50 border border-yellow-200 p-3 text-sm space-y-1">
              <p><span className="font-medium">Email:</span> {addedEmail}</p>
              <p><span className="font-medium">Temp password:</span> <span className="font-mono">{addedTempPw}</span></p>
            </div>
            <p className="text-xs text-gray-500">Give these to the user. They should change their password after first login.</p>
          </div>
        ) : (
          <form id="add-staff-form" onSubmit={handleAdd} className="space-y-4">
            <Input
              label="Email address"
              type="email"
              value={addForm.email}
              onChange={(e) => setAddForm((f) => ({ ...f, email: e.target.value }))}
              placeholder="doctor@clinic.com"
              required
            />
            <Select
              label="Role"
              value={addForm.role}
              onChange={(e) => setAddForm((f) => ({ ...f, role: e.target.value as StaffRole }))}
            >
              <option value={StaffRole.ADMIN}>Admin — Facility Manager</option>
              <option value={StaffRole.FRONT_DESK}>Front Desk / Reception</option>
              <option value={StaffRole.PROVIDER}>Provider / Consultant</option>
            </Select>
            <Input
              label="Temporary Password (min 8 chars)"
              type="text"
              value={addForm.tempPassword}
              onChange={(e) => setAddForm((f) => ({ ...f, tempPassword: e.target.value }))}
              placeholder="e.g. Clinic@2024"
              required
              minLength={8}
            />
            <p className="text-xs text-gray-500">
              User is created immediately — no email sent. Share the credentials directly with the staff member.
            </p>
            {addError && <p className="text-sm text-red-600">{addError}</p>}
          </form>
        )}
      </Modal>

      {/* Reset password link modal */}
      <Modal
        open={!!resetLinkFor}
        title={`Reset Password — ${resetLinkFor?.email ?? ''}`}
        onClose={() => { setResetLinkFor(null); setResetLink(null); }}
        footer={<Button variant="secondary" onClick={() => { setResetLinkFor(null); setResetLink(null); }}>Close</Button>}
      >
        {resetLinkLoading && <p className="text-sm text-gray-500">Generating link…</p>}
        {resetLinkError && <p className="text-sm text-red-600">{resetLinkError}</p>}
        {resetLink && (
          <div className="space-y-3">
            <p className="text-sm text-gray-600">Copy this link and send it to the user via WhatsApp or SMS. It expires after one use.</p>
            <div className="flex gap-2 items-center">
              <input
                readOnly
                value={resetLink}
                className="flex-1 rounded border border-gray-300 bg-gray-50 px-2 py-1.5 text-xs font-mono text-gray-700"
              />
              <Button
                size="sm"
                variant="secondary"
                onClick={() => { navigator.clipboard.writeText(resetLink); setResetLinkCopied(true); }}
              >
                {resetLinkCopied ? 'Copied!' : 'Copy'}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Edit modal */}
      <Modal
        open={showEdit}
        title={`Edit — ${editingMember?.email ?? editingMember?.userId ?? ''}`}
        onClose={() => { setShowEdit(false); setEditError(null); }}
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowEdit(false)}>Cancel</Button>
            <Button form="edit-staff-form" type="submit" loading={editSaving}>Save</Button>
          </>
        }
      >
        <form id="edit-staff-form" onSubmit={handleEdit} className="space-y-4">
          <Select
            label="Role"
            value={editForm.role}
            onChange={(e) => setEditForm((f) => ({ ...f, role: e.target.value as StaffRole }))}
          >
            <option value={StaffRole.ADMIN}>Admin — Facility Manager</option>
            <option value={StaffRole.FRONT_DESK}>Front Desk / Reception</option>
            <option value={StaffRole.PROVIDER}>Provider / Consultant</option>
          </Select>
          <Select
            label="Status"
            value={editForm.isActive ? 'true' : 'false'}
            onChange={(e) => setEditForm((f) => ({ ...f, isActive: e.target.value === 'true' }))}
          >
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </Select>
          {editError && <p className="text-sm text-red-600">{editError}</p>}
        </form>
      </Modal>
    </div>
  );
}
